/**
 * CalendarEventPicker — modal that pulls the signed-in user's Outlook
 * calendar via Graph (`GET /calendar/events`) and lets them pick a meeting
 * to prefill the New Meeting form.
 *
 * The modal opens over the New Meeting form, fetches events in a past-7 /
 * next-3 day window by default, and groups them by local date. Clicking an
 * event calls onPick(event) with everything the form needs (subject, date,
 * location, online-meeting hint). Parent component decides how to map
 * that into form state.
 *
 * On 401 (reauth required) the modal surfaces a clear "Sign in again"
 * message instead of a generic error. Any other failure is shown with the
 * raw message so IT / the user can diagnose scope issues.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { GraphErrorBanner } from '../GraphErrorBanner.tsx';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '../ui/Modal.tsx';

/**
 * Lookback ladder for infinite-scroll. Each rung roughly doubles the
 * window so a user with sparse calendar history can reach a meeting
 * a few months back without an explicit picker. Cap at 180 days to
 * bound the api cost — beyond that, narrowing the range is a better
 * UX than scrolling further.
 */
const LOOKBACK_LADDER = [14, 30, 60, 90, 180] as const;

export interface CalendarEvent {
  id: string;
  subject: string;
  start: string;
  end: string;
  localDate: string;
  location: string | null;
  attendees: Array<{ name: string; email: string; type: 'required' | 'optional' | 'resource' }>;
  organizer: { name: string; email: string } | null;
  isOnlineMeeting: boolean;
  isCancelled: boolean;
  seriesMasterId: string | null;
  webLink: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPick: (event: CalendarEvent) => void;
}

export function CalendarEventPicker({ isOpen, onClose, onPick }: Props) {
  // ladderIdx walks LOOKBACK_LADDER as the user scrolls to the bottom
  // of the list. Starts at 0 (14 days). Each bump triggers a new
  // wider-window query; keepPreviousData keeps the list visible
  // during the transition so the user's scroll position doesn't jump.
  const [ladderIdx, setLadderIdx] = useState(0);
  const daysBack = LOOKBACK_LADDER[ladderIdx];
  const atMaxRange = ladderIdx >= LOOKBACK_LADDER.length - 1;

  // Reset to the first rung whenever the modal closes — next open is
  // a fresh look at the most-recent window.
  useEffect(() => {
    if (!isOpen) setLadderIdx(0);
  }, [isOpen]);

  const range = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
    return { from: toIsoDate(from), to: toIsoDate(now) };
  }, [daysBack]);

  const events = useQuery({
    queryKey: ['calendar-events', range.from, range.to],
    queryFn: () =>
      api.get<{
        events: CalendarEvent[];
        truncated?: boolean;
        pagesFetched?: number;
        rawCount?: number;
        filteredCount?: number;
      }>('/calendar/events', {
        from: range.from,
        to: range.to,
        // The api now paginates @odata.nextLink up to 10 pages
        // (2000 events) and filters out PTO/OOO/free-busy on the
        // server side, so this client-side limit is the
        // PER-PAGE size. Generous so dense calendars still
        // resolve in 1-2 pages.
        limit: 300,
      }),
    enabled: isOpen,
    retry: false,
    // Keep the previous result visible while a wider-range fetch is
    // in flight — prevents the list from blanking out mid-scroll.
    placeholderData: keepPreviousData,
  });

  // IntersectionObserver-based auto-load. When the sentinel scrolls
  // into view AND we have more rungs left AND we're not already
  // fetching, advance the ladder. Single-shot per intersection (the
  // ref guards against double-fires while the next page loads).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const advanceQueuedRef = useRef(false);
  useEffect(() => {
    if (atMaxRange) return;
    const node = sentinelRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (
          e.isIntersecting &&
          !advanceQueuedRef.current &&
          !events.isFetching
        ) {
          advanceQueuedRef.current = true;
          setLadderIdx((i) => Math.min(i + 1, LOOKBACK_LADDER.length - 1));
        }
      },
      { root: null, rootMargin: '100px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [atMaxRange, events.isFetching, events.data]);

  // Clear the single-shot guard when the wider-range fetch lands so
  // a future scroll can trigger the next rung.
  useEffect(() => {
    if (!events.isFetching) advanceQueuedRef.current = false;
  }, [events.isFetching]);

  if (!isOpen) return null;

  // The api now classifies Graph errors via classifyGraphError
  // and returns { code, title, advice, retryAfterSeconds } on
  // every failure. We hand the error straight to GraphErrorBanner
  // which picks the right tone (terra-cotta for action-required,
  // amber for wait-and-retry, red for general). reauthNeeded
  // stays as a derived flag because some downstream UI hides
  // entirely when the user must re-sign-in.
  const reauthNeeded =
    events.error instanceof ApiError &&
    (events.error.status === 401 ||
      (typeof events.error.data === 'object' &&
        events.error.data !== null &&
        'code' in events.error.data &&
        (events.error.data as { code?: string }).code === 'reauth'));
  const hasError = events.error != null;

  // Group by localDate
  const grouped = new Map<string, CalendarEvent[]>();
  for (const e of events.data?.events ?? []) {
    const list = grouped.get(e.localDate) ?? [];
    list.push(e);
    grouped.set(e.localDate, list);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Pick from Outlook calendar"
    >
      <ModalHeader
        title="Pick from Outlook calendar"
        subtitle="Click an event to prefill the form. Scroll for older meetings."
        onClose={onClose}
      />

      {/* Status strip — shows the current window + a loading hint when
          a wider-range fetch is in flight. No date-range picker — the
          list auto-loads more history as you scroll past the bottom. */}
      <div className="flex items-center gap-3 border-b border-charcoal/10 bg-cloud/40 px-6 py-2.5 text-[11px]">
        <span className="text-charcoal/65">
          Showing past <span className="font-semibold text-charcoal">{daysBack}</span> days
        </span>
        {events.isFetching && (
          <span className="ml-auto italic text-charcoal/55">Loading…</span>
        )}
      </div>

      <ModalBody>
        {hasError && (
          <GraphErrorBanner
            err={events.error}
            contextAdvice={
              reauthNeeded
                ? 'Sign out and sign in again to refresh calendar access. (Also needed the first time OJB adds Calendars.Read to the app.)'
                : undefined
            }
          />
        )}

        {!hasError && events.isLoading && (
          <div className="py-10 text-center text-xs italic text-charcoal/55">
            Fetching your events…
          </div>
        )}

        {!hasError &&
          !events.isLoading &&
          events.data &&
          events.data.events.length === 0 && (
            <div className="py-10 text-center text-xs italic text-charcoal/55">
              No events in this window. Try widening the range.
            </div>
          )}

        {/* Truncation hint — surfaced when the api hit its
            @odata.nextLink page cap (10 pages = 2000 events).
            Tells the user the picker isn't showing everything
            in the window so they can narrow the date range to
            find what they're looking for. */}
        {!hasError && events.data?.truncated && (
          <div className="mb-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
            <div className="font-semibold">Showing the most recent meetings</div>
            <p className="mt-0.5 leading-relaxed">
              You have a lot of events in this window. The list shows the
              most recent {daysBack}-day batch - older meetings beyond the
              page cap aren&rsquo;t included. Open Outlook directly if you
              need to find one.
            </p>
          </div>
        )}

        {!hasError && events.data && events.data.events.length > 0 && (
          <div className="space-y-5">
            {/* Most recent day first. Within a day, times sort ascending
                (7 AM → 5 PM) because that reads naturally, but the day
                headers march backwards — today at top, older below.
                The IntersectionObserver-watched sentinel sits AFTER
                this list to auto-load the next ladder rung. */}
            {[...grouped.entries()]
              .sort(([a], [b]) => (a < b ? 1 : -1))
              .map(([date, dayEvents]) => {
                const sortedDay = [...dayEvents].sort((a, b) =>
                  a.start < b.start ? -1 : 1,
                );
                return (
                  <div key={date}>
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-pine">
                      {formatDay(date)}
                    </div>
                    <ul className="overflow-hidden rounded-2xl border border-charcoal/10 bg-paper-light">
                      {sortedDay.map((e, i) => (
                        <li
                          key={e.id}
                          className={
                            'group ' + (i > 0 ? 'border-t border-charcoal/10' : '')
                          }
                        >
                          <button
                            type="button"
                            onClick={() => {
                              onPick(e);
                              onClose();
                            }}
                            title={
                              e.isCancelled
                                ? 'This event was cancelled - click to use it anyway'
                                : undefined
                            }
                            className={
                              'flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-pine/5 ' +
                              (e.isCancelled ? 'opacity-55' : '')
                            }
                          >
                            <span className="min-w-[58px] shrink-0 text-[11px] tabular-nums text-charcoal/60">
                              {formatTime(e.start)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <div
                                className={
                                  'truncate text-sm font-medium ' +
                                  (e.isCancelled
                                    ? 'text-charcoal/70 line-through'
                                    : 'text-charcoal')
                                }
                              >
                                {e.subject || '(no subject)'}
                              </div>
                              <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-charcoal/60">
                                {e.isCancelled && (
                                  <span className="font-semibold uppercase tracking-[0.14em] text-red-700/80">
                                    Cancelled
                                  </span>
                                )}
                                {e.isOnlineMeeting && <span>Online meeting</span>}
                                {e.location && <span>{e.location}</span>}
                                {e.attendees.length > 0 && (
                                  <span>
                                    {e.attendees.length} attendee
                                    {e.attendees.length === 1 ? '' : 's'}
                                  </span>
                                )}
                                {e.seriesMasterId && (
                                  <span className="rounded-full bg-pine/10 px-2 py-0.5 text-[10px] font-medium text-pine">
                                    Recurring
                                  </span>
                                )}
                              </div>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

            {/* Auto-load sentinel — when scrolled into view, advance to
                the next rung of LOOKBACK_LADDER. Hidden when we're at
                the last rung; shows a "scroll to load" hint while in
                flight. */}
            {!atMaxRange && (
              <div
                ref={sentinelRef}
                className="flex items-center justify-center pt-1 pb-2 text-[11px] italic text-charcoal/50"
              >
                {events.isFetching
                  ? 'Loading older meetings…'
                  : 'Scroll for older meetings'}
              </div>
            )}
            {atMaxRange && (
              <div className="pt-1 pb-2 text-center text-[11px] italic text-charcoal/45">
                Reached {LOOKBACK_LADDER[LOOKBACK_LADDER.length - 1]}-day limit. Older meetings
                aren&rsquo;t shown - open Outlook directly if you need them.
              </div>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="text-[11px] text-charcoal/60">
        Subject, date, format, location, and attendees prefill from the
        picked event. Edit attendees on the form before creating the draft.
      </ModalFooter>
    </Modal>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map((p) => Number.parseInt(p, 10));
  const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
